/**
 * ExpressionDecomposition — breaks a master item or variable into every level it
 * references (tree), the text Qlik's $() would produce (final expression) and the
 * items that use it. Presentation only: the parent builds the graph from its
 * effective items (pending drafts applied) and passes `key={itemId}` so the tab,
 * source and open rows reset when the selection changes. Hovering a reference in a
 * definition lights up the row that opens it; hovering a row lights up its reference.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ChevronRight, Copy } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  FeedbackState,
  Mono,
  Segmented,
  Tabs,
  type BadgeProps,
} from "@h2j-data-team/ui";
import {
  decompose,
  usedBy,
  type DecompositionNodeKind,
  type DecompositionRow,
  type ExpressionGraph,
  type FlatSegmentKind,
  type GraphItemKind,
  type GraphRef,
  type GraphSourceId,
} from "@repo/shared";
import { toast } from "@/components/shared/toast";
import { cn } from "@/lib/utils";

/**
 * Detail panel body around the decomposition (the panel is the `@container`): the form
 * and the decomposition stack, then sit side by side once the panel is wide enough.
 */
export const DETAIL_GRID =
  "grid max-w-[720px] gap-x-8 px-5 py-5 @min-[1040px]:max-w-none @min-[1040px]:grid-cols-[minmax(0,600px)_minmax(0,1fr)]";

export interface ExpressionDecompositionProps {
  graph: ExpressionGraph;
  itemId: string;
  /** "group": the graph holds only the group's items; unknown `$(name)` shows as outside. */
  scope: "app" | "group";
  onOpenItem: (kind: GraphItemKind, itemId: string) => void;
}

type Tab = "tree" | "flat" | "usedBy";

const KIND_BADGE: Record<DecompositionNodeKind, NonNullable<BadgeProps["variant"]>> = {
  measure: "info",
  variable: "neutral",
  calc: "warning",
  param: "secondary",
  outside: "outline",
  missing: "danger",
};

const ITEM_BADGE: Record<GraphItemKind, NonNullable<BadgeProps["variant"]>> = {
  measure: "info",
  variable: "neutral",
  dimension: "secondary",
};

const SEGMENT_CLASS: Record<FlatSegmentKind, string> = {
  own: "",
  variable: "bg-brand-subtle text-brand-text",
  calc: "bg-status-warning-bg text-status-warning-foreground",
  measure: "bg-status-info-bg text-status-info-foreground underline underline-offset-2",
  outside: "text-dim underline decoration-dashed underline-offset-2",
  missing: "bg-status-danger-bg text-status-danger-foreground",
};

const LEGEND: FlatSegmentKind[] = ["own", "variable", "calc", "measure"];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Splits `text` so the referenced names can be underlined. A name written as `$(name)`
 * or `[name]` takes its wrapper along, so the whole token is what lights up.
 */
function splitRefs(text: string, names: string[]): { text: string; ref: string | null }[] {
  const unique = [...new Set(names)].sort((a, b) => b.length - a.length);
  if (!text || unique.length === 0) return [{ text, ref: null }];
  const re = new RegExp(
    `(?<![\\p{L}\\p{N}_])(?:${unique.map(escapeRegex).join("|")})(?![\\p{L}\\p{N}_])`,
    "gu",
  );
  const parts: { text: string; ref: string | null }[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    let start = m.index;
    let end = start + m[0].length;
    if (start - 2 >= last && text.slice(start - 2, start) === "$(" && text[end] === ")") {
      start -= 2;
      end += 1;
    } else if (start - 1 >= last && text[start - 1] === "[" && text[end] === "]") {
      start -= 1;
      end += 1;
    }
    if (start > last) parts.push({ text: text.slice(last, start), ref: null });
    parts.push({ text: text.slice(start, end), ref: m[0] });
    last = end;
  }
  if (last < text.length) parts.push({ text: text.slice(last), ref: null });
  return parts;
}

function Definition({
  text,
  refs,
  open,
  hot,
  onHot,
  owner,
}: {
  text: string;
  /** Child rows by the name that references them. */
  refs: Map<string, number[]>;
  open: boolean;
  hot: number | null;
  onHot: (index: number | null) => void;
  /** Row holding this definition (null for the decomposed source); hover returns to it. */
  owner: number | null;
}) {
  return (
    <Mono
      title={open ? undefined : text}
      className={cn(
        "block text-[12px] leading-[18px] text-dim",
        open ? "whitespace-pre-wrap" : "truncate",
      )}
    >
      {splitRefs(text, [...refs.keys()]).map((part, i) => {
        if (!part.ref) return <span key={i}>{part.text}</span>;
        const targets = refs.get(part.ref) ?? [];
        return (
          <span
            key={i}
            onMouseEnter={() => onHot(targets[0] ?? null)}
            onMouseLeave={() => onHot(owner)}
            className={cn(
              "rounded-sm font-medium text-brand-text underline underline-offset-2",
              hot !== null && targets.includes(hot) && "bg-brand-subtle ring-1 ring-brand",
            )}
          >
            {part.text}
          </span>
        );
      })}
    </Mono>
  );
}

export function ExpressionDecomposition({
  graph,
  itemId,
  scope,
  onOpenItem,
}: ExpressionDecompositionProps) {
  const { t, i18n } = useTranslation("single-app");
  const item = graph.items.get(itemId);

  // The main expression always shows; label and fields only when they reference something.
  const sources = useMemo(
    () =>
      (item?.sources ?? []).filter(
        (s) => s.id === "expression" || (item?.refs.get(s.id)?.length ?? 0) > 0,
      ),
    [item],
  );
  const [tab, setTab] = useState<Tab>("tree");
  const [sourceId, setSourceId] = useState<GraphSourceId | null>(null);
  const source = sources.find((s) => s.id === sourceId) ?? sources[0];
  const [open, setOpen] = useState<Record<string, boolean>>({});
  // Row under the pointer, or opened by the reference under it.
  const [hot, setHot] = useState<number | null>(null);

  const result = useMemo(
    () => (source ? decompose(graph, itemId, source.id) : null),
    [graph, itemId, source],
  );
  const users = useMemo(() => usedBy(graph, itemId), [graph, itemId]);

  // Nothing to break down yet (new item without expression, plain-field dimension).
  if (!item || !source || !result || item.sources.every((s) => !s.text.trim())) return null;

  const sourceLabel = (id: GraphSourceId) =>
    id === "expression"
      ? t(
          item.kind === "variable"
            ? "decomposition.sources.definition"
            : "decomposition.sources.expression",
        )
      : id === "label"
        ? t("decomposition.sources.label")
        : t("decomposition.sources.field", { n: Number(id.slice("field:".length)) + 1 });

  const { rows } = result;
  const childRefs = (index: number | null) => {
    const refs = new Map<string, number[]>();
    rows.forEach((r, i) => {
      if (r.parentIndex === index) refs.set(r.name, [...(refs.get(r.name) ?? []), i]);
    });
    return refs;
  };
  /** `index` sits somewhere under `ancestor`. */
  const isUnder = (index: number, ancestor: number) => {
    for (let p = rows[index]?.parentIndex ?? null; p !== null; p = rows[p]?.parentIndex ?? null) {
      if (p === ancestor) return true;
    }
    return false;
  };
  const openKey = (index: number) => `${source.id}:${index}`;
  const isOpen = (row: DecompositionRow, index: number) =>
    open[openKey(index)] ?? (row.childCount > 0 && !row.repeated && !row.cycle);
  const setAll = (value: boolean) =>
    setOpen((prev) => {
      const next = { ...prev };
      rows.forEach((_, i) => (next[openKey(i)] = value));
      return next;
    });

  const visible: { row: DecompositionRow; index: number; open: boolean }[] = [];
  let hideBelow: number | null = null;
  for (const [index, row] of rows.entries()) {
    if (hideBelow !== null && row.level > hideBelow) continue;
    hideBelow = null;
    const rowOpen = isOpen(row, index);
    if (row.childCount > 0 && !rowOpen) hideBelow = row.level;
    visible.push({ row, index, open: rowOpen });
  }

  const formatNumber = new Intl.NumberFormat(i18n.language).format;
  const flatText = result.flat.map((s) => s.text).join("");
  const copyFlat = () =>
    navigator.clipboard
      .writeText(flatText)
      .then(() => toast.success(t("decomposition.flat.copied")))
      .catch(() => toast.error(t("decomposition.flat.copyFailed")));

  const openButton = (kind: GraphItemKind, id: string, name: string) => (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={t("decomposition.open", { name })}
      title={t("decomposition.open", { name })}
      onClick={() => onOpenItem(kind, id)}
    >
      <ArrowUpRight />
    </Button>
  );

  const sourcePicker =
    sources.length > 1 ? (
      <Segmented<GraphSourceId>
        size="compact"
        label={t("decomposition.source")}
        value={source.id}
        onValueChange={(id) => {
          setSourceId(id);
          setHot(null);
        }}
        options={sources.map((s) => ({ value: s.id, label: sourceLabel(s.id) }))}
      />
    ) : source.id !== "expression" ? (
      <span className="text-[12px] font-medium text-dim">{sourceLabel(source.id)}</span>
    ) : null;

  const tree = (
    <div className="flex flex-col gap-2.5 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {sourcePicker}
          {rows.length > 0 && (
            <span className="font-mono text-[12px] text-dim">
              {t("decomposition.levels", { count: result.levels })} ·{" "}
              {t("decomposition.items", { count: result.uniqueItems })}
            </span>
          )}
        </div>
        {rows.some((r) => r.childCount > 0) && (
          <div className="flex gap-1">
            <Button variant="ghost" size="compact" onClick={() => setAll(true)}>
              {t("decomposition.expandAll")}
            </Button>
            <Button variant="ghost" size="compact" onClick={() => setAll(false)}>
              {t("decomposition.collapseAll")}
            </Button>
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <FeedbackState state="empty" title={t("decomposition.tree.empty")} />
      ) : (
        // ponytail: plain list; the largest tree in the reference app has ~100 rows. Virtualize if that grows.
        <ul
          aria-label={t("decomposition.tabs.tree")}
          className="overflow-hidden rounded-lg border border-hairline"
          onMouseLeave={() => setHot(null)}
        >
          <li
            className="flex flex-col gap-1 bg-surface-muted px-3 py-2"
            onMouseEnter={() => setHot(null)}
          >
            <div className="flex items-center gap-2">
              <Badge variant={source.id === "label" ? "outline" : ITEM_BADGE[item.kind]}>
                {source.id === "label"
                  ? t("decomposition.sources.label")
                  : t(`decomposition.itemKind.${item.kind}`)}
              </Badge>
              <Mono className="text-[13px] font-semibold text-ink">{item.name}</Mono>
            </div>
            <Definition
              text={source.text}
              refs={childRefs(null)}
              open
              hot={hot}
              onHot={setHot}
              owner={null}
            />
          </li>
          {visible.map(({ row, index, open: rowOpen }) => {
            const canToggle =
              !row.repeated && !row.cycle && (row.childCount > 0 || !!row.definition);
            const target = row.itemId ? graph.items.get(row.itemId) : undefined;
            // The hovered row and its subtree light up; the path back to the root stays sharp.
            const lit = hot !== null && (index === hot || isUnder(index, hot));
            const onPath = hot !== null && isUnder(hot, index);
            return (
              <li
                key={index}
                className={cn(
                  "flex border-t border-hairline-soft transition-[opacity,background-color]",
                  lit && (index === hot ? "bg-brand-subtle" : "bg-brand-subtle/50"),
                  hot !== null && !lit && !onPath && "opacity-40",
                )}
                style={{ paddingLeft: 4 + row.level * 22 }}
                onMouseEnter={() => setHot(index)}
              >
                <div
                  className={cn(
                    "flex min-w-0 flex-1 items-start gap-1.5 py-2 pl-2 pr-3",
                    row.level > 0 && "border-l border-hairline",
                  )}
                >
                  {canToggle ? (
                    <Button
                      variant="ghost"
                      size="icon-compact"
                      aria-expanded={rowOpen}
                      aria-label={t(rowOpen ? "decomposition.collapse" : "decomposition.expand", {
                        name: row.name,
                      })}
                      onClick={() => setOpen((prev) => ({ ...prev, [openKey(index)]: !rowOpen }))}
                    >
                      <ChevronRight
                        className={cn("transition-transform", rowOpen && "rotate-90")}
                      />
                    </Button>
                  ) : (
                    <span className="size-7 shrink-0" aria-hidden />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex min-h-7 flex-wrap items-center gap-2">
                      <Badge variant={KIND_BADGE[row.kind]}>
                        {t(`decomposition.kind.${row.kind}`)}
                      </Badge>
                      <Mono className="text-[13px] font-medium text-ink">{row.name}</Mono>
                      {row.bare && (
                        <Badge variant="outline" title={t("decomposition.bareHint")}>
                          {t("decomposition.bare")}
                        </Badge>
                      )}
                      {row.args.length > 0 && (
                        <Mono className="text-[12px] text-brand-text">
                          {row.args.map((a, i) => `$${i + 1} = ${a}`).join(", ")}
                        </Mono>
                      )}
                      <span className="ml-auto flex items-center gap-2">
                        {row.repeated && (
                          <span className="text-[12px] text-faint">
                            {t("decomposition.repeated")}
                          </span>
                        )}
                        {row.cycle && (
                          <span className="text-[12px] text-faint">{t("decomposition.cycle")}</span>
                        )}
                        {scope === "app" && target && !row.repeated && row.usedByCount > 0 && (
                          <span className="font-mono text-[11px] text-faint">
                            {t("decomposition.usedIn", { count: row.usedByCount })}
                          </span>
                        )}
                        {target && openButton(target.kind, target.id, row.name)}
                      </span>
                    </div>
                    {row.definition && !row.repeated && (
                      <Definition
                        text={row.definition}
                        refs={childRefs(index)}
                        open={rowOpen}
                        hot={hot}
                        onHot={setHot}
                        owner={index}
                      />
                    )}
                    {rowOpen && row.kind === "calc" && (
                      <p className="m-0 text-[12px] text-status-warning-foreground">
                        {t("decomposition.calcNote")}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const flat = (
    <div className="flex flex-col gap-2.5 pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {sourcePicker}
          <span className="font-mono text-[12px] text-dim">
            {t("decomposition.flat.length", {
              from: formatNumber(source.text.length),
              to: formatNumber(flatText.length),
            })}
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={copyFlat}>
          <Copy />
          {t("decomposition.flat.copy")}
        </Button>
      </div>
      <ul
        aria-label={t("decomposition.flat.legend")}
        className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1.5 p-0 text-[12px] text-dim"
      >
        {[...LEGEND, ...(scope === "group" ? (["outside"] as const) : [])].map((kind) => (
          <li key={kind} className="inline-flex items-center gap-1.5">
            <span
              className={cn("size-3 rounded-sm border border-hairline", SEGMENT_CLASS[kind])}
              aria-hidden
            />
            {t(`decomposition.flat.kind.${kind}`)}
          </li>
        ))}
      </ul>
      <pre className="m-0 max-h-[420px] overflow-auto rounded-lg border border-hairline bg-surface px-4 py-3.5 font-mono text-[12px] leading-5 whitespace-pre-wrap break-all text-ink">
        {result.flat.map((segment, i) => (
          <span
            key={i}
            title={
              segment.origin ? t("decomposition.flat.origin", { name: segment.origin }) : undefined
            }
            className={cn("rounded-sm", SEGMENT_CLASS[segment.kind])}
          >
            {segment.text}
          </span>
        ))}
      </pre>
      <p className="m-0 text-[12px] text-faint">{t("decomposition.flat.note")}</p>
    </div>
  );

  const userList = (list: (GraphRef & { via?: string })[]) => (
    <ul className="overflow-hidden rounded-lg border border-hairline">
      {list.map((u) => (
        <li
          key={u.itemId}
          className="flex flex-wrap items-center gap-2 border-t border-hairline-soft py-1.5 pl-3 pr-2 first:border-t-0"
        >
          <Badge variant={ITEM_BADGE[u.kind]}>{t(`decomposition.itemKind.${u.kind}`)}</Badge>
          <Mono className="text-[13px] font-medium text-ink">{u.name}</Mono>
          {u.via && (
            <span className="text-[12px] text-faint">
              {t("decomposition.usedBy.via")} <Mono>{u.via}</Mono>
            </span>
          )}
          <span className="flex-1" />
          {openButton(u.kind, u.itemId, u.name)}
        </li>
      ))}
    </ul>
  );

  const usedByTotal = users.direct.length + users.indirect.length;
  const usedByPane = (
    <div className="flex flex-col gap-4 pt-3">
      {usedByTotal === 0 ? (
        <FeedbackState
          state="empty"
          title={t(
            scope === "group" ? "decomposition.usedBy.emptyGroup" : "decomposition.usedBy.empty",
          )}
        />
      ) : (
        <>
          {users.direct.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <h3 className="m-0 text-[12px] font-semibold text-dim">
                {t("decomposition.usedBy.direct")}{" "}
                <span className="font-mono font-normal text-faint">{users.direct.length}</span>
              </h3>
              {userList(users.direct)}
            </section>
          )}
          {users.indirect.length > 0 && (
            <section className="flex flex-col gap-1.5">
              <h3 className="m-0 text-[12px] font-semibold text-dim">
                {t("decomposition.usedBy.indirect")}{" "}
                <span className="font-mono font-normal text-faint">{users.indirect.length}</span>
              </h3>
              {userList(users.indirect)}
            </section>
          )}
          <p className="m-0 text-[12px] text-faint">
            {t("decomposition.usedBy.impact", { name: item.name, count: usedByTotal })}
          </p>
        </>
      )}
    </div>
  );

  return (
    <section
      aria-label={t("decomposition.title")}
      className="mt-6 flex min-w-0 flex-col gap-3 border-t border-hairline pt-5 @min-[1040px]:mt-0 @min-[1040px]:border-t-0 @min-[1040px]:border-l @min-[1040px]:pt-0 @min-[1040px]:pl-8"
    >
      <span className="font-mono text-[10px] uppercase tracking-widest text-faint">
        {t("decomposition.title")}
      </span>
      {scope === "group" && <Alert tone="info">{t("decomposition.groupScope")}</Alert>}
      {result.truncated && <Alert tone="warning">{t("decomposition.truncated")}</Alert>}
      <Tabs
        label={t("decomposition.title")}
        value={tab}
        onValueChange={(v) => setTab(v as Tab)}
        items={[
          {
            value: "tree",
            label: t("decomposition.tabs.tree"),
            content: tab === "tree" ? tree : null,
          },
          {
            value: "flat",
            label: t("decomposition.tabs.flat"),
            content: tab === "flat" ? flat : null,
          },
          ...(item.kind === "dimension"
            ? []
            : [
                {
                  value: "usedBy",
                  label: (
                    <>
                      {t("decomposition.tabs.usedBy")}{" "}
                      <span className="font-mono text-[11px] text-faint">{usedByTotal}</span>
                    </>
                  ),
                  content: tab === "usedBy" ? usedByPane : null,
                },
              ]),
        ]}
      />
    </section>
  );
}
